import React, { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, Save, Scale } from 'lucide-react';
import type { ClienteComercialDetalhe, ConflitoClienteComercial, ContatoFiscalCliente } from '@pieltec/contracts';
import { obterSessaoAtual } from '../../lib/session';
import { CamposContatoCliente } from './cliente-form';
import { apiFetch, apiMutate } from '../../lib/csrf-fetch';

const inputClass = 'w-full min-h-11 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10';

function descreverCampos(dados: Readonly<Record<string, unknown>>): string {
  const labels: Record<string, string> = { nome: 'Razão social', nomeFantasia: 'Nome fantasia', contato: 'Nome do contato', email: 'E-mail', telefone1Ddd: 'DDD principal', telefone1Numero: 'Telefone principal', telefone2Ddd: 'DDD secundário', telefone2Numero: 'Telefone secundário' };
  return Object.entries(dados).flatMap(([campo, valor]) => campo === 'contatoFiscal' && valor && typeof valor === 'object'
    ? descreverCampos(valor as Record<string, unknown>).split('\n')
    : [`${labels[campo] ?? campo}: ${valor == null || valor === '' ? '(vazio)' : typeof valor === 'string' || typeof valor === 'number' || typeof valor === 'boolean' ? String(valor) : '(vazio)'}`]).join('\n');
}

function idDaRota(): string | null {
  const partes = window.location.pathname.split('/');
  const indice = partes.indexOf('clientes');
  return indice >= 0 ? partes[indice + 1] ?? null : null;
}

export const EditarClientePage: React.FC = () => {
  const clienteId = idDaRota();
  const [cliente, setCliente] = useState<ClienteComercialDetalhe | null>(null);
  const [nome, setNome] = useState('');
  const [nomeFantasia, setNomeFantasia] = useState('');
  const [contato, setContato] = useState<ContatoFiscalCliente>({});
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [podeGerenciar, setPodeGerenciar] = useState(false);
  useEffect(() => { void obterSessaoAtual().then((sessao) => setPodeGerenciar(sessao.autenticado && sessao.usuario.permissions.includes('cadastros:gerenciar'))); }, []);
  const [conflito, setConflito] = useState<ConflitoClienteComercial | null>(null);

  const carregar = async (preservarEdicao = false): Promise<void> => {
    if (!clienteId) { setErro('Cliente inválido.'); return; }
    try {
      const resposta = await apiFetch<ClienteComercialDetalhe>(`/api/v1/cadastros/clientes/${encodeURIComponent(clienteId)}/comercial`);
      setCliente(resposta);
      if (!preservarEdicao) {
        setNome(resposta.nome); setNomeFantasia(resposta.nomeFantasia ?? ''); setContato(resposta.contatoFiscal ?? {});
      }
      if (resposta.estadoIntegracaoOmie === 'CONFLITO' && resposta.alteracaoPendenteId) {
        const detalhe = await apiFetch<ConflitoClienteComercial>(`/api/v1/cadastros/clientes/${encodeURIComponent(resposta.id)}/comercial/conflitos/${encodeURIComponent(resposta.alteracaoPendenteId)}`);
        setConflito(detalhe);
      } else setConflito(null);
    } catch { setErro('Não foi possível carregar a ficha comercial do cliente.'); }
  };
  useEffect(() => { void carregar(); }, [clienteId]);
  useEffect(() => {
    if (cliente?.estadoIntegracaoOmie !== 'PENDENTE') return undefined;
    const timer = window.setInterval(() => { void carregar(true); }, 15000);
    return () => window.clearInterval(timer);
  }, [cliente?.estadoIntegracaoOmie, clienteId]);

  const salvar = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!cliente || !clienteId || !podeGerenciar) return;
    setSalvando(true); setErro(null);
    try {
      const body: Record<string, unknown> = { versaoEsperada: cliente.versaoCadastro };
      if (nome !== cliente.nome) body.nome = nome;
      if (nomeFantasia !== (cliente.nomeFantasia ?? '')) body.nomeFantasia = nomeFantasia || null;
      const contatoAlterado = Object.fromEntries(Object.entries(contato)
        .filter(([campo, valor]) => (valor ?? '') !== (cliente.contatoFiscal?.[campo as keyof ContatoFiscalCliente] ?? ''))
        .map(([campo, valor]) => [campo, valor === '' ? null : valor]));
      if (Object.keys(contatoAlterado).length) body.contatoFiscal = contatoAlterado;
      if (Object.keys(body).length === 1) { setErro('Nenhum campo foi alterado.'); return; }
      const resposta = await apiMutate(`/api/v1/cadastros/clientes/${encodeURIComponent(clienteId)}/comercial`, {
        method: 'PATCH', body,
      });
      if (!resposta.ok) {
        const corpo = await resposta.json().catch(() => ({})) as { message?: string };
        throw new Error(corpo.message ?? 'Não foi possível salvar a alteração.');
      }
      await carregar();
    } catch (causa) { setErro(causa instanceof Error ? causa.message : 'Falha ao salvar alteração.');
    } finally { setSalvando(false); }
  };

  const resolverConflito = async (decisao: 'ACEITAR_OMIE' | 'MANTER_PIELTEC'): Promise<void> => {
    if (!clienteId || !conflito || !podeGerenciar) return;
    setSalvando(true); setErro(null);
    try {
      const resposta = await apiMutate(`/api/v1/cadastros/clientes/${encodeURIComponent(clienteId)}/comercial/conflitos/${encodeURIComponent(conflito.alteracaoId)}/resolver`, {
        method: 'POST', body: { decisao, versaoEsperada: conflito.versaoEsperada },
      });
      if (!resposta.ok) throw new Error('O conflito mudou enquanto era resolvido. Recarregue a ficha.');
      await carregar(false);
    } catch (causa) { setErro(causa instanceof Error ? causa.message : 'Não foi possível resolver o conflito.');
    } finally { setSalvando(false); }
  };

  if (erro && !cliente) return <main className="p-6" role="alert">{erro}</main>;
  if (!cliente) return <main className="p-6 flex items-center gap-2"><Loader2 className="animate-spin" /> Carregando cliente…</main>;
  const pendente = cliente.estadoIntegracaoOmie === 'PENDENTE';
  return <main className="mx-auto max-w-3xl p-4 md:p-6">
    <a className="inline-flex min-h-11 items-center text-sm font-semibold text-slate-700 underline" href="/cadastros">Voltar aos cadastros</a>
    <section className="mt-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm md:p-6">
      <h1 className="text-xl font-bold text-slate-900">Editar cliente</h1>
      <p className="mt-1 text-sm text-slate-600">Nome, nome fantasia e contato principal. Dados fiscais e financeiros são mantidos pela Omie.</p>
      {cliente.estadoIntegracaoOmie === 'FALHA_SINCRONIZACAO' && <p className="mt-4 flex items-center gap-2 text-sm text-rose-700"><AlertTriangle className="h-4 w-4" /> Falha na sincronização. Revise os dados e tente novamente.</p>}
      {cliente.estadoIntegracaoOmie === 'SOMENTE_LOCAL' && <p className="mt-4 text-sm text-slate-600">Cadastro somente local.</p>}
      {!podeGerenciar && <p className="mt-4 text-sm text-slate-600">Seu perfil permite somente consulta deste cadastro.</p>}
      {pendente && <p className="mt-4 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"><Loader2 className="h-4 w-4 animate-spin" /> Alteração enviada; aguardando confirmação da Omie.</p>}
      {conflito && <section className="mt-4 rounded-md border border-rose-200 bg-rose-50 p-4" aria-labelledby="conflito-cliente-titulo">
        <h2 id="conflito-cliente-titulo" className="flex items-center gap-2 text-sm font-bold text-rose-900"><Scale className="h-4 w-4" /> Conflito de sincronização</h2>
        <p className="mt-1 text-sm text-rose-800">A Omie e a Pieltec alteraram o mesmo campo. Escolha qual versão deve continuar.</p>
        <div className="mt-3 grid gap-3 text-sm md:grid-cols-2">
          <div className="rounded border border-rose-200 bg-white p-3"><strong>Pieltec</strong><pre className="mt-1 overflow-auto whitespace-pre-wrap text-xs">{descreverCampos(conflito.desejado)}</pre></div>
          <div className="rounded border border-rose-200 bg-white p-3"><strong>Omie</strong><pre className="mt-1 overflow-auto whitespace-pre-wrap text-xs">{descreverCampos(conflito.remoto)}</pre></div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" className="min-h-11 rounded-md border border-slate-400 bg-white px-4 text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900" disabled={salvando || !podeGerenciar} onClick={() => void resolverConflito('ACEITAR_OMIE')}>Aceitar Omie</button>
          <button type="button" className="min-h-11 rounded-md bg-rose-700 px-4 text-sm font-semibold text-white focus:outline-none focus:ring-2 focus:ring-rose-900" disabled={salvando || !podeGerenciar} onClick={() => void resolverConflito('MANTER_PIELTEC')}>Manter Pieltec</button>
        </div>
      </section>}
      {erro && <p className="mt-4 flex items-center gap-2 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800" role="alert"><AlertTriangle className="h-4 w-4" />{erro}</p>}
      <form className="mt-5 space-y-4" onSubmit={(event) => void salvar(event)}>
        <label className="block text-sm font-semibold">Razão social<input className={inputClass} required value={nome} onChange={(e) => setNome(e.target.value)} /></label>
        <label className="block text-sm font-semibold">Nome fantasia<input className={inputClass} value={nomeFantasia} onChange={(e) => setNomeFantasia(e.target.value)} /></label>
        <CamposContatoCliente contato={contato} onChange={(campo, valor) => setContato({ ...contato, [campo]: valor })} />
        <button className="inline-flex min-h-11 items-center gap-2 rounded-md bg-teal-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60" disabled={salvando || pendente || !!conflito || !podeGerenciar} type="submit">
          {salvando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}{salvando ? 'Salvando…' : cliente.codigoClienteOmie ? 'Salvar e sincronizar' : 'Salvar cliente'}
        </button>
      </form>
      {cliente.estadoIntegracaoOmie === 'SINCRONIZADO' && <p className="mt-4 flex items-center gap-2 text-sm text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Sincronizado com a Omie.</p>}
    </section>
  </main>;
};
